import { Mark, markPasteRule, mergeAttributes } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
/* eslint-disable unicorn/consistent-function-scoping */
export interface LinkOptions {
  openOnClick: boolean

  linkOnPaste: boolean

  HTMLAttributes: Record<string, any>
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    link: {
      unsetLink: () => ReturnType
    }
  }
}

/**
 * A regex that matches any string that contains a link
 */
export const pasteRegex = /ht{2}ps?:\/{2}(?:w{3}\.)?[\w#%+.:=@~-]{1,256}\.[a-z]{2,}\b(?:[\w!#%&+./:=?@~-]*){2}/gi

/**
 * A regex that matches an url
 */
export const pasteRegexExact = /^ht{2}ps?:\/{2}(?:w{3}\.)?[\w#%+.:=@~-]{1,256}\.[a-z]{2,}\b(?:[\w!#%&+./:=?@~-]*){2}$/gi

export const Link = Mark.create<LinkOptions>({
  name: 'link',

  priority: 1000,

  inclusive: false,

  addOptions() {
    return {
      openOnClick: true,
      linkOnPaste: true,
      HTMLAttributes: {
        target: '_blank',
        rel: 'noopener noreferrer nofollow',
      },
    }
  },

  addAttributes() {
    return {
      href: {
        default: null,
      },
      target: {
        default: this.options.HTMLAttributes.target,
      },
    }
  },

  parseHTML() {
    return [{ tag: 'a[href]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['a', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0]
  },

  addCommands() {
    return {
      unsetLink:
        () =>
        ({ commands }) => {
          return commands.unsetMark('link', {
            extendEmptyMarkRange: true,
          })
        },
    }
  },

  addPasteRules() {
    return [
      markPasteRule({
        find: pasteRegex,
        type: this.type,
        getAttributes: (match) => ({ href: match[0] }),
      }),
    ]
  },

  addProseMirrorPlugins() {
    const plugins = []

    if (this.options.openOnClick) {
      plugins.push(
        new Plugin({
          key: new PluginKey('handleClickLink'),
          props: {
            handleClick: (view, pos, event) => {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore

              const attrs = this.editor.getAttributes('link')
              const link = (event.target as HTMLElement)?.closest('a')

              if (event.button !== 2 && link && attrs.href) {
                window.open(attrs.href, attrs.target)
                return true
              }

              return false
            },
          },
        })
      )
    }

    if (this.options.linkOnPaste) {
      plugins.push(
        new Plugin({
          key: new PluginKey('handlePasteLink'),
          props: {
            handlePaste: (view, event, slice) => {
              const { state } = view
              const { selection } = state
              const { empty } = selection

              if (empty) {
                return false
              }

              let textContent = ''

              slice.content.forEach((node) => {
                textContent += node.textContent
              })

              if (!textContent || !pasteRegexExact.test(textContent)) {
                return false
              }

              this.editor.commands.setMark(this.type, {
                href: textContent,
              })

              return true
            },
          },
        })
      )
    }

    return plugins
  },
})
